import { ptlEnvs } from "@/tz/go/egaz/osci/ptl/comn";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { comnUtils, comnEnvs } from "@/comn/utils";
import { Page, Group, Layout, Button, Tab, Tree, Grid } from "@/comn/components";
import {
    useForm,
    useFetch,
    useToast,
    useModal,
    useResource,
    useTree,
    usePage,
    TFormSchema,
    useAuth,
    useGrid,
    useStore,
} from "@/comn/hooks";
import { BASE, URLS, APIS, SF_ORG_USR_INFO, SG_USR_AUTH_LIST } from "./services/OsciOrgUsrService";

/*
 * Organization User Management Detail
 * !== 기관 사용자 관리 상세 조회 ==!
 */
export const OSCI0103017Q = (props: any) => {
    const { pgeUid, menuId } = usePage({
        auth: ["auth_osci0002"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { usrIdntfId } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid });
    const auth = useAuth();

    useResource({
        defaultSchema: [
            { area: "orgDeptCd" },
            { area: "cntyCd" },
            { area: "orgCd" },
            { area: "comnCd", comnCd: "OSC0156" },
            { area: "cstmCd" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Organization User Information Form !== 기관 사용자 정보 form ==!
        orgUsrInfo: useForm({
            defaultSchema: SF_ORG_USR_INFO,
            defaultValues: {},
        }),
    };

    const grid = {
        ogaUsrAuthList: useGrid({ defaultSchema: SG_USR_AUTH_LIST }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Check Organization User Information !== 기관 사용자 정보 조회 ==!
        getOrgUsrInfo: useFetch({
            api: (data) => APIS.getOrgUsrInfo(usrIdntfId),
            enabled: !!usrIdntfId,
            onSuccess: (data) => {
                const orgUsrInfo = data.orgUsrInfo.content;
                form.orgUsrInfo.setValues(orgUsrInfo);
                fetch.getOgaUsrAuthList.fetch;
            },
            onError: (error) => {},
            showToast: true,
        }),

        getOgaUsrAuthList: useFetch({
            api: (page = grid.ogaUsrAuthList.page, size = grid.ogaUsrAuthList.size) =>
                APIS.getOgaUsrAuthList({ usrIdntfId: usrIdntfId }, page, size),
            enabled: true,
            key: [grid.ogaUsrAuthList.page, grid.ogaUsrAuthList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    page: grid.ogaUsrAuthList.page,
                    size: grid.ogaUsrAuthList.size,
                });
            },
            onError: () => modal.openModal({ content: "msg.osc.00043" }), // Failed to select. !== 조회가 실패하였습니다. ==!
        }),

        // Delete Organization User Information !== 기관 사용자 정보 삭제 ==!
        deleteOrgUsrInfo: useFetch({
            api: (usrIdntfId) => APIS.deleteOrgUsrInfo(usrIdntfId),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.osc.00006",
                    onCancel: () => {
                        navigate(`${URLS.osci0103014q}?menuId=${menuId}`);
                    },
                });
            },
            onError: (error) => {},
            showToast: true,
        }),

        //  !== 기관 사용자 비밀번호를 초기화한다. ==!
        updateOrgUsrPwdInit: useFetch({
            api: (data) => APIS.updateOrgUsrPwdInit(data),
            onSuccess: (data) => {
                modal.openModal({
                    content: (
                        <div>
                            {t("L_NEW_PWD")} : {data?.randomUsrPwd?.content}
                        </div>
                    ),
                });
            },
            onError: (error) => {
                const data = error.response.data;
                modal.openModal({
                    content: data.errorMessage,
                });
            },
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Delete Organization User Information !== 기관 사용자 정보 삭제 ==!
        deleteOrgUsrInfo: () => {
            modal.openModal({
                content: "msg.osc.00095",
                onConfirm: () => {
                    fetch.deleteOrgUsrInfo.fetch(usrIdntfId);
                },
            });
        },

        onResetBtnClick: () => {
            modal.openModal({
                content: "msg.osc.00221", // Do you want to reset your password? !== 비밀번호를 초기화 하시겠습니까? ==!
                onConfirm: () => {
                    if (!comnUtils.isEmpty(form.orgUsrInfo.errors)) {
                        toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                        return;
                    }
                    form.orgUsrInfo.handleSubmit(
                        () => {
                            const usrAplyInfo = form.orgUsrInfo.getValues();

                            fetch.updateOrgUsrPwdInit.fetch(usrAplyInfo);
                        },
                        // () => {
                        //     toast.showToast({ type: "warning", content: "msg.osc.00003" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                        // },
                    )();
                },
            });
        },
    };
    const [ogaRppnYn, setOgaRppnYn] = useState(auth.get("orgRppnYn"));

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        fetch.getOrgUsrInfo.fetch();
        form.orgUsrInfo.setEditable(false);
        grid.ogaUsrAuthList.setOption("checkbox", false);
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.psnTin} />
                            <Group.Control {...form.orgUsrInfo.schema.usrNm} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.orgCd} />
                            <Group.Control {...form.orgUsrInfo.schema.deptCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.orgRppnYn} />
                            {/* <Group.Control {...form.orgUsrInfo.schema.frgnrYn} /> */}
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.aplcCntyCd} />
                            <Group.Control {...form.orgUsrInfo.schema.usrMoblNo} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.usrEmlAddr} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.orgUsrInfo.schema.rsdtAddr} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            {/* <Group.Control {...form.orgUsrInfo.schema.cstmOfceCd} /> */}
                            <Group.Control {...form.orgUsrInfo.schema.usrStatCd} />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Title title={"T_OGA_USR_AUTH_LIST"} />
                    <Group.Section gap={2}>
                        <Grid {...grid.ogaUsrAuthList.grid} data={fetch.getOgaUsrAuthList.data?.authList} />
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(`${URLS.osci0103014q}?menuId=${menuId}`);
                        }}
                    ></Button>
                </Layout.Left>
                <Layout.Right>
                    <Button
                        role={"reset"}
                        onClick={() => {
                            handler.onResetBtnClick();
                        }}
                    >
                        {t("B_RESET_PWD")}
                    </Button>
                    <Layout.Button
                        role={"edit"}
                        onClick={() => {
                            navigate(`${URLS.osci0103016s}/${usrIdntfId}`);
                        }}
                    >
                        {t("B_EDIT")}
                    </Layout.Button>
                    <Layout.Button
                        role={"delete"}
                        onClick={() => {
                            handler.deleteOrgUsrInfo();
                        }}
                    >
                        {t("B_DEL")}
                    </Layout.Button>
                </Layout.Right>
            </Layout>
        </Page>
    );
};

/*
 * !== 반려사유 Modal ==!
 */
export const ModalRtrnRsn = (props: any) => {
    useResource({
        defaultSchema: [{ area: "bsopClsfCd" }, { area: "comnCd", comnCd: "OSC0088" }],
    });

    const { data, handlePopup } = props;

    // 현재 화면설계 참고 -> 항목과 검색어 입력폼 명은 개발에 맞게 변경 해주세요(현재는 라벨이없어서 임시로 넣어놓았습니다)
    const SG_FORM: TFormSchema = {
        id: "form",
        schema: {
            rtrnRsn: { type: "textarea", label: "L_RTRN_RSN", required: true, controlSize: 10, maxLength: 2000 },
        },
    };

    const form = useForm({
        defaultSchema: SG_FORM,
        defaultValues: { ...data },
    });

    const handler = {
        submitBtnClick: () => {
            handlePopup(form.getValue("rtrnRsn"));
        },
    };

    return (
        <>
            <Page>
                <Group>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.schema.rtrnRsn} />
                            </Group.Row>
                        </Group.Section>
                        <Group.Section>
                            <Layout>
                                <Layout.Right>
                                    <Button
                                        role={"submit"}
                                        onClick={() => {
                                            handler.submitBtnClick();
                                        }}
                                    />
                                </Layout.Right>
                            </Layout>
                        </Group.Section>
                    </Group.Body>
                </Group>
            </Page>
        </>
    );
};
